import { useNavigate, useParams } from "react-router-dom";
import { getAuthHeaders, PATH_INFO } from "../../util/helper";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Badge, Button, Col, Descriptions, Divider, Flex, List, Modal, Row, Space, Spin, Table, Upload } from "antd";
import Column from "antd/es/table/Column";
import type { TablePaginationConfig } from "antd/es/table";
import { CaretRightFilled, DatabaseOutlined, DownloadOutlined, EditOutlined, FileAddOutlined, LeftOutlined, PauseOutlined, ProfileOutlined, ReconciliationOutlined, SlidersOutlined, UserAddOutlined } from "@ant-design/icons";
import { parse as parseContentDisposition } from "@tinyhttp/content-disposition";
import { CompanyContext, UpdateCompanyContext } from "../../../context/companyContext";
import { UpdateUsersContext, UserResponseType } from "../../../context/userContext";
import type { DepartmentResponseType } from "../../../context/departmentContext";

const DEPARTMENT_PAGE_SIZE = 5;
const USER_PAGE_SIZE = 5;

type PaginationState = {
  current: number;
  pageSize: number;
  total: number;
};

type UserMetaState = {
  active: number;
};

export function CompanyInfo() {
  const { company } = useContext(CompanyContext);
  const { refreshCompany, toggleCompanyStatus } = useContext(UpdateCompanyContext);
  const { toggleUserDisabled } = useContext(UpdateUsersContext);
  const { companyId } = useParams();
  const navigate = useNavigate();

  const [departmentList, setDepartmentList] = useState<Partial<DepartmentResponseType>[]>([]);
  const [departmentPagination, setDepartmentPagination] = useState<PaginationState>({ current: 1, pageSize: DEPARTMENT_PAGE_SIZE, total: 0 });
  const [userList, setUserList] = useState<UserResponseType[]>([]);
  const [userPagination, setUserPagination] = useState<PaginationState>({ current: 1, pageSize: USER_PAGE_SIZE, total: 0 });
  const [userMeta, setUserMeta] = useState<UserMetaState>({ active: 0 });
  const [currentAntdSortField, setCurrentAntdSortField] = useState<string | string[] | undefined>(undefined);
  const [currentAntdSortOrder, setCurrentAntdSortOrder] = useState<'ascend' | 'descend' | undefined>(undefined);
  const [currentDepartmentSortOrder, setCurrentDepartmentSortOrder] = useState<'ascend' | 'descend' | undefined>(undefined);
  const [currentDepartmentSortField, setCurrentDepartmentSortField] = useState<string | undefined>(undefined);

  const [isLoadingCompany, setIsLoadingCompany] = useState<boolean>(true);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState<boolean>(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingUsersCsv, setIsDownloadingUsersCsv] = useState(false);

  const fetchDepartments = useCallback(async (page?: number, pageSize?: number, sortField?: string, sortOrder?: 'asc' | 'desc') => {
    if (!companyId) {
      setDepartmentList([]);
      setDepartmentPagination({ current: 1, pageSize: pageSize ?? DEPARTMENT_PAGE_SIZE, total: 0 });
      setIsLoadingDepartments(false);
      return;
    }

    setIsLoadingDepartments(true);

    const targetPage = page ?? 1;
    const targetPageSize = pageSize ?? DEPARTMENT_PAGE_SIZE;

    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": "application/json",
      })
      if (!headers) {
        return
      }

      let url = `${process.env.REACT_APP_API_ENDPOINT}/apis/admin/departments/company/${companyId}?page=${targetPage}&limit=${targetPageSize}`;
      if (sortField) {
        url += `&sort_by=${sortField}`;
      }
      if (sortOrder) {
        url += `&order=${sortOrder}`;
      }
      const res = await fetch(url, {
        headers,
      });

      if (res.status === 401) {
        sessionStorage.removeItem("token");
        navigate(PATH_INFO.LOGIN.PATH);
        return;
      }

      if (!res.ok) {
        throw new Error("Failed to fetch departments");
      }

      const data = await res.json();
      const list = (Array.isArray(data) ? data : data.data ?? []) as Partial<DepartmentResponseType>[];
      const meta = Array.isArray(data) ? undefined : data.meta ?? {};

      setDepartmentList(list);
      setDepartmentPagination({
        current: meta?.page ?? targetPage,
        pageSize: meta?.limit ?? targetPageSize,
        total: meta?.total ?? list.length,
      });
    } catch (error) {
      console.error("Error fetching departments.", error);
      setDepartmentList([]);
      setDepartmentPagination((prev) => ({
        ...prev,
        current: 1,
        total: 0,
      }));
    } finally {
      setIsLoadingDepartments(false);
    }
  }, [companyId, navigate]);

  const fetchUsers = useCallback(async (page?: number, pageSize?: number, sortField?: string, sortOrder?: string) => {
    if (!companyId) {
      setUserList([]);
      setUserPagination({ current: 1, pageSize: pageSize ?? USER_PAGE_SIZE, total: 0 });
      setUserMeta({ active: 0 });
      setIsLoadingUsers(false);
      return;
    }

    setIsLoadingUsers(true);

    const targetPage = page ?? 1;
    const targetPageSize = pageSize ?? USER_PAGE_SIZE;

    let url = `${process.env.REACT_APP_API_ENDPOINT}/apis/admin/users/company/${companyId}?page=${targetPage}&limit=${targetPageSize}`;
    if (sortField && sortOrder) {
      url += `&sortField=${sortField}&sortOrder=${sortOrder}`;
    }
    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": "application/json",
      })
      if (!headers) {
        return
      }

      const res = await fetch(url, {
        headers,
      });

      if (res.status === 401) {
        sessionStorage.removeItem("token");
        navigate(PATH_INFO.LOGIN.PATH);
        return;
      }

      if (!res.ok) {
        throw new Error("Failed to fetch users");
      }

      const data = await res.json();
      const list = (Array.isArray(data) ? data : data.data ?? []) as UserResponseType[];
      const meta = Array.isArray(data) ? undefined : data.meta ?? {};

      setUserList(list);
      setUserPagination({
        current: meta?.page ?? targetPage,
        pageSize: meta?.limit ?? targetPageSize,
        total: meta?.total ?? list.length,
      });
      setUserMeta({
        active: meta?.active ?? list.filter((user) => !user.is_disabled).length,
      });
    } catch (error) {
      console.error("Error fetching users.", error);
      setUserList([]);
      setUserPagination((prev) => ({
        ...prev,
        current: 1,
        total: 0,
      }));
      setUserMeta({ active: 0 });
    } finally {
      setIsLoadingUsers(false);
    }
  }, [companyId, navigate]);

  useEffect(() => {
    setDepartmentList([]);
    setDepartmentPagination({ current: 1, pageSize: DEPARTMENT_PAGE_SIZE, total: 0 });
    setUserList([]);
    setUserPagination({ current: 1, pageSize: USER_PAGE_SIZE, total: 0 });
    setCurrentAntdSortField(undefined);
    setCurrentAntdSortOrder(undefined);
    setCurrentDepartmentSortOrder(undefined);
    setCurrentDepartmentSortField(undefined);
    setUserMeta({ active: 0 });

    setIsLoadingCompany(true);

    if (companyId) {
      refreshCompany(companyId).finally(() => {
        setIsLoadingCompany(false);
      });
      fetchDepartments(1, DEPARTMENT_PAGE_SIZE);
      fetchUsers(1, USER_PAGE_SIZE);
    } else {
      setIsLoadingCompany(false);
      setIsLoadingDepartments(false);
      setIsLoadingUsers(false);
    }
  }, [companyId, fetchDepartments, fetchUsers, refreshCompany]);

  const handleDepartmentTableChange = useCallback((pagination: TablePaginationConfig, _filters: any, sorter: any) => {
    const nextPage = pagination.current ?? 1;
    const nextPageSize = pagination.pageSize ?? DEPARTMENT_PAGE_SIZE;
    let sortOrder: 'asc' | 'desc' | undefined;
    let antdSortOrder: 'ascend' | 'descend' | undefined;
    let antdSortField: string | undefined;
    if (sorter && sorter.field && sorter.order) {
      antdSortOrder = sorter.order;
      antdSortField = sorter.field;
      if (antdSortOrder === 'ascend') {
        sortOrder = 'asc';
      } else if (antdSortOrder === 'descend') {
        sortOrder = 'desc';
      }
    }
    setCurrentDepartmentSortOrder(antdSortOrder);
    setCurrentDepartmentSortField(antdSortField);
    fetchDepartments(nextPage, nextPageSize, antdSortField, sortOrder);
  }, [fetchDepartments]);

  const handleUserTableChange = useCallback((pagination: TablePaginationConfig, _filters: any, sorter: any) => {
    const nextPage = pagination.current ?? 1;
    const nextPageSize = pagination.pageSize ?? USER_PAGE_SIZE;

    let backendSortField: string | undefined;
    let backendSortOrder: string | undefined;
    let antdSortField: string | string[] | undefined;
    let antdSortOrder: 'ascend' | 'descend' | undefined;

    if (sorter && sorter.field && sorter.order) {
      antdSortField = sorter.field;
      antdSortOrder = sorter.order;

      if (Array.isArray(antdSortField) && antdSortField[0] === 'department' && antdSortField[1] === 'name') {
        backendSortField = 'departmentname';
      } else if (Array.isArray(antdSortField) && antdSortField[0] === 'department' && antdSortField[1] === 'brand_name') {
        backendSortField = 'brand_name';
      } else if (antdSortField === 'name') {
        backendSortField = 'username';
      } else {
        backendSortField = antdSortField as string;
      }

      if (antdSortOrder === 'ascend') {
        backendSortOrder = 'ASC';
      } else if (antdSortOrder === 'descend') {
        backendSortOrder = 'DESC';
      }
    }
    setCurrentAntdSortField(antdSortField);
    setCurrentAntdSortOrder(antdSortOrder);
    fetchUsers(nextPage, nextPageSize, backendSortField, backendSortOrder);
  }, [fetchUsers, setCurrentAntdSortField, setCurrentAntdSortOrder]);

  const formatNumber = useCallback((num: string) => {
    if (num.length === 11) {
      return `${num.slice(0, 3)}-${num.slice(3, 7)}-${num.slice(7)}`;
    }
    if (num.length === 10) {
      return `${num.slice(0, 4)}-${num.slice(4, 7)}-${num.slice(7)}`;
    }
    return num;
  }, []);

  const DATA_PLACEHOLDER = useMemo(() => {
    if (isLoadingCompany || !company) {
      return [];
    }
    return [
      {
        title: "企業情報",
        column: [
          { key: "company_name", label: "企業名", value: company?.company_name ?? "" },
          { key: "address", label: "住所", value: company?.address ?? "" },
          { key: "phone_number", label: "電話番号", value: formatNumber((company?.phone_number ?? "").toString()) },
          { key: "post_code", label: "郵便番号", value: company?.post_code ?? "" },
          { key: "toyoshima_sales_department", label: "豊島営業部", value: company?.toyoshima_sales_department ?? "" },
          { key: "toyoshima_sales_rep_name", label: "豊島営業担当者名", value: company?.toyoshima_sales_rep_name ?? "" },
        ],
      },
      {
        title: "導入予定拠点",
        column: [
          { key: "planned_site_name", label: "拠点名", value: company?.planned_site_name ?? "" },
          { key: "planned_site_address", label: "住所", value: company?.planned_site_address ?? "" },
          { key: "planned_site_phone", label: "電話番号", value: formatNumber((company?.planned_site_phone ?? "").toString()) },
          { key: "planned_site_post_code", label: "郵便番号", value: company?.planned_site_post_code ?? "" },
          { key: "planned_site_email", label: "メールアドレス", value: company?.planned_site_email ?? "" },
          { key: "planned_site_contact_name", label: "担当者名", value: company?.planned_site_contact_name ?? "" },
          { key: "planned_site_contact_position", label: "担当者役職", value: company?.planned_site_contact_position ?? "" },
        ],
      },
      {
        title: "窓口担当者",
        column: [
          { key: "contact_department", label: "部署名", value: company?.contact_department ?? "" },
          { key: "contact_name", label: "氏名", value: company?.contact_name ?? "" },
          { key: "contact_position", label: "役職", value: company?.contact_position ?? "" },
          { key: "contact_email", label: "メールアドレス", value: company?.contact_email ?? "" },
          { key: "contact_phone", label: "電話番号", value: formatNumber((company?.contact_phone ?? "").toString()) },
        ],
      },
      {
        title: "請求先情報",
        column: [
          { key: "billing_department", label: "部署名", value: company?.billing_department ?? "" },
          { key: "billing_contact", label: "氏名", value: company?.billing_contact ?? "" },
          { key: "blilling_position", label: "役職", value: company?.billing_position ?? "" },
          { key: "billing_email", label: "メールアドレス", value: company?.billing_email ?? "" },
          { key: "billing_phone", label: "電話番号", value: formatNumber((company?.billing_phone ?? "").toString()) },
        ],
      },
      {
        title: "契約情報",
        column: [
          { key: "contract_start_date", label: "開始日", value: company?.contract_start_date ?? "" },
          { key: "contract_end_date", label: "終了予定日", value: company?.contract_end_date ?? "" },
          {
            key: "contract_amount",
            label: "販売金額",
            value: new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY", minimumFractionDigits: 0 }).format(company?.contract_amount ?? 0),
          },
        ],
      },
      {
        title: "企業メモ",
        column: [
          { key: "company_memo", label: "メモ", value: company?.company_memo ?? "" },
        ],
      },
    ];
  }, [company, formatNumber, isLoadingCompany]);


  const onClickDepartment = useCallback((departmentId: string) => {
    if (!companyId) return;
    const result = PATH_INFO.DEPARTMENT_DETAIL.PATH.replace(":companyId", companyId);
    const destination = result.replace(":departmentId", departmentId);
    navigate(destination);
  }, [companyId, navigate]);

  const onClickMeasurementsError = useCallback(() => {
    if (!companyId) return;
    const destination = PATH_INFO.MEASUREMENTS_ERROR.PATH.replace(":companyId", companyId);
    navigate(destination);
  }, [companyId, navigate]);

  const onClickDeviceList = useCallback(() => {
    if (!companyId) return;
    const destination = PATH_INFO.COMPANY_DEVICES_LIST.PATH.replace(":companyId", companyId);
    navigate(destination);
  }, [companyId, navigate]);

  const onClickMeasurements = useCallback(() => {
    if (!companyId) return;
    const destination = PATH_INFO.COMPANY_MEASUREMENTS_LIST.PATH.replace(":companyId", companyId);
    navigate(destination);
  }, [companyId, navigate]);

  const getOnDepartmentCell = (record: { id: string }) => ({
    onClick: () => onClickDepartment(record.id),
    style: { cursor: "pointer" },
  });

  const onClickManage = useCallback((departmentId?: number, userId?: string) => {
    if (!companyId || !departmentId || !userId) return;
    const result = PATH_INFO.DEPARTMENT_USER_MANAGE.PATH.replace(":companyId", companyId);
    const destination = result.replace(":departmentId", `${departmentId}`).replace(":userId", userId);
    navigate(destination);
  }, [companyId, navigate]);

  const onClickToggleStatus = useCallback(async (userId?: string) => {
    if (!userId) return;

    const originalState = userList.find((user) => user.id === userId)?.is_disabled;

    setUserList((prev) =>
      prev.map((user) =>
        user.id === userId ? { ...user, is_disabled: !user.is_disabled } : user
      )
    );

    try {
      await toggleUserDisabled(userId);
      await fetchUsers(userPagination.current, userPagination.pageSize);
    } catch {
      setUserList((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, is_disabled: originalState } : user
        )
      );
    }
  }, [fetchUsers, toggleUserDisabled, userList, userPagination]);

  const onClickUserRegister = useCallback(() => {
    if (!companyId) return;
    const result = PATH_INFO.DEPARTMENT_USER_MANAGE.PATH.replace(":companyId", companyId);
    const destination = result.replace(":departmentId", "undefined").replace(":userId", "register");
    navigate(destination);
  }, [companyId, navigate]);

  const onClickUser = useCallback((departmentId?: number, userId?: string) => {
    if (!companyId || !userId) return;
    const result = PATH_INFO.DEPARTMENT_USER_DETAIL.PATH.replace(":companyId", companyId);
    const destination = result.replace(":departmentId", `${departmentId}`).replace(":userId", userId);
    navigate(destination);
  }, [companyId, navigate]);

  const getOnUserCell = useCallback((record: UserResponseType) => ({
    onClick: () => onClickUser(record.department.id, record.id),
    style: {
      background: record.is_disabled ? "rgba(0, 0, 0, 0.04)" : "",
      color: record.is_disabled ? "rgba(0,0,0,0.5)" : "",
      cursor: "pointer",
    },
  }), [onClickUser]);

  const onClickCompanyManage = useCallback(() => {
    if (!companyId) return;
    const destination = PATH_INFO.COMPANY_MANAGE.PATH.replace(":companyId", companyId);
    navigate(destination);
  }, [companyId, navigate]);

  const activeUserValue = userMeta.active;
  const totalUserValue = userPagination.total;

  const onUserUploadCsv = useCallback(async (options: any) => {
    const { file, onSuccess, onError } = options;

    if (!companyId) return;

    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    const showUploadErrorModal = (apiResult?: any) => {
      const errorItems = Array.isArray(apiResult?.errors) ? apiResult.errors : [];
      Modal.error({
        centered: true,
        title: apiResult?.message ?? 'アップロードに失敗しました。',
        content: (
          <div>
            {errorItems.length > 0 && (
              <div>
                <div>
                  <ul className="list-disc pl-4">
                    {errorItems.map((item: { row?: number; message?: string }, index: number) => (
                      <li key={`${item.row ?? index}`}>
                        {item.row ? `${item.row}行目: ` : ""}
                        {item.message ?? "詳細不明のエラーです。"}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        ),
      });
    };

    try {
      const headers = getAuthHeaders(navigate)
      if (!headers) {
        onError("error")
        return
      }

      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/users/upload/company/${companyId}`, {
        method: "POST",
        headers,
        body: formData,
      });

      const result = await response.json();
      if (response.ok) {
        Modal.success({ content: `${file.name}のアップロードに成功しました`, centered: true });
        await fetchUsers(userPagination.current, userPagination.pageSize);
        onSuccess(result);
      } else {
        showUploadErrorModal(result ?? undefined);
        onError("error");
      }
    } catch (_error) {
      showUploadErrorModal();
      onError("error");
    } finally {
      setIsUploading(false);
    }
  }, [companyId, fetchUsers, navigate, userPagination]);

  const onUploadCsv = useCallback(async (options: any) => {
    const { file, onSuccess, onError } = options;
    if (!companyId) return;

    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const headers = getAuthHeaders(navigate)
      if (!headers) {
        onError("error")
        return
      }

      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/departments/upload/${companyId}`, {
        method: "POST",
        headers,
        body: formData,
      });

      const result = await response.json();
      if (response.ok) {
        Modal.success({ content: `${file.name}のアップロードに成功しました`, centered: true });
        await refreshCompany(companyId);
        await fetchDepartments(1, departmentPagination.pageSize);
        onSuccess(result);
      } else {
        Modal.error({ content: `${file.name}のアップロードに失敗しました`, centered: true });
        onError("error");
      }
    } catch (error) {
      Modal.error({ content: `${file.name}のアップロードに失敗しました`, centered: true });
      onError("error");
    } finally {
      setIsUploading(false);
    }
  }, [companyId, departmentPagination.pageSize, fetchDepartments, navigate, refreshCompany]);

  const onDownloadUsersCsv = useCallback(async () => {
    if (!companyId) return;

    const extractFileName = (contentDispositionHeader?: string | null) => {
      if (!contentDispositionHeader) {
        return undefined;
      }

      try {
        const parsed = parseContentDisposition(contentDispositionHeader); // Content-Dispositionヘッダーを既存ライブラリで解析
        const filenameStar = typeof parsed.parameters?.["filename*"] === "string" ? parsed.parameters["filename*"] : undefined;
        if (filenameStar) {
          return filenameStar;
        }

        const rawName = typeof parsed.parameters?.filename === "string" ? parsed.parameters.filename : undefined;
        if (!rawName) {
          return undefined;
        }

        if (rawName.includes("%")) {
          // filename*（encoding付き）ではなく解析後の通常のrawNameで%が混在する場合のみ追加デコードを試行する
          try {
            return decodeURIComponent(rawName);
          } catch (_error) {
            return rawName;
          }
        }

        return rawName;
      } catch (_error) {
        return undefined;
      }
    };

    setIsDownloadingUsersCsv(true);
    try {
      const headers = getAuthHeaders(navigate)
      if (!headers) {
        return
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/companies/${companyId}/users/template`, {
        method: "GET",
        headers,
      });

      if (res.status === 401) {
        sessionStorage.removeItem("token");
        navigate(PATH_INFO.LOGIN.PATH);
        return;
      }

      if (!res.ok) {
        throw new Error("Failed to download users CSV");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = res.headers.get("Content-Disposition");
      const fallbackName = `users-template.csv`;
      link.href = url;
      link.setAttribute("download", extractFileName(disposition) ?? fallbackName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading users CSV.", error);
      Modal.error({ content: "CSVのダウンロードに失敗しました。", centered: true });
    } finally {
      setIsDownloadingUsersCsv(false);
    }
  }, [companyId, navigate]);

  return (
    <div className="mb-20">
      <Spin spinning={isUploading} tip="CSVアップロード中..." size="large" fullscreen />
      <Button variant="text" color="primary" icon={<LeftOutlined />} onClick={() => navigate(-1)} className="mb-2 -ml-3">戻る</Button>
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">企業詳細画面</p>
        <Button variant="solid" color="primary" icon={<EditOutlined />} onClick={onClickCompanyManage}>企業・部署を編集</Button>
        <Button
          variant="outlined"
          color={company?.is_disabled ? "green" : "danger"}
          icon={company?.is_disabled ? <CaretRightFilled /> : <PauseOutlined />}
          onClick={() => {
            if (!companyId) return;
            toggleCompanyStatus(parseInt(companyId, 10));
          }}
        >
          {company?.is_disabled ? '復活' : '停止'}
        </Button>
      </div>
      <Divider />
      {isLoadingCompany ? (
        <Flex justify="center" align="center">
          <Spin size="large" tip='Loading...' />
        </Flex>
      ) : (
        <Row justify="center" align="top" className="w-full" gutter={[24, 24]}>
          {DATA_PLACEHOLDER.map((section) => (
            <Col xs={12} xl={6} key={section.title}>
              <List
                header={<div className="font-bold">{section.title}</div>}
                bordered
                dataSource={section.column}
                renderItem={(item) => (
                  section.title === '企業メモ' ? (
                    <div className='overflow-y-auto max-h-[235px]'>
                      <List.Item style={{ background: '#FFFFFF' }}>
                        <Descriptions column={1} size="small" layout="vertical">
                          <Descriptions.Item label={item.label} key={item.key}>
                            <p className={`m-0 pl-2 whitespace-pre-wrap ${item.value ? '' : 'text-black/25'}`}>{item.value || 'データなし'}</p>
                          </Descriptions.Item>
                        </Descriptions>
                      </List.Item>
                    </div>
                  ) : (
                    <List.Item style={{ background: '#FFFFFF' }}>
                      <Descriptions column={1} size="small" layout="vertical">
                        <Descriptions.Item label={item.label} key={item.key}>
                          <p className={`m-0 pl-2 ${item.value ? '' : 'text-black/25'}`}>{item.value || 'データなし'}</p>
                        </Descriptions.Item>
                      </Descriptions>
                    </List.Item>
                  )
                )}
                className={`overflow-hidden bg-[#FAFAFA] ${section.title === '企業メモ' ? 'max-h-[285px]' : ''}`}
              />
            </Col>
          ))}
        </Row>
      )}
      <Divider />
      <div className="flex p-2 text-xl mb-3">
        <ProfileOutlined className="text-[#1677ff]" />
        <span className="pl-2">部署一覧</span>
      </div>
      <div className="mb-1 pr-5 flex justify-end w-full">
        <Badge status="processing" color="blue" />
        <span className="pl-1">部署合計数: {departmentPagination.total}</span>
      </div>
      <Flex vertical justify="center" className="relative">
        {isLoadingDepartments ? (
          <Flex justify="center" align="center">
            <Spin size="large" tip='Loading...' />
          </Flex>
        ) : (
          <Table
            dataSource={departmentList}
            bordered
            pagination={{
              current: departmentPagination.current,
              pageSize: departmentPagination.pageSize,
              total: departmentPagination.total,
              position: ['bottomCenter'],
            }}
            onChange={handleDepartmentTableChange}
            rowKey={(record) => record.id || ''}
          >
            <Column
              title='部署名'
              dataIndex="name"
              key="name"
              onCell={getOnDepartmentCell}
              sorter={true}
              sortOrder={currentDepartmentSortField === 'name' ? currentDepartmentSortOrder : undefined}
            />
            <Column
              title='ブランド名'
              dataIndex='brand_name'
              key='brand_name'
              onCell={getOnDepartmentCell}
              sorter={true}
              sortOrder={currentDepartmentSortField === 'brand_name' ? currentDepartmentSortOrder : undefined}
            />
            <Column
              title='メモ'
              dataIndex='memo'
              key='department_memo'
              className="whitespace-pre-wrap"
              onCell={getOnDepartmentCell}
              render={(text) => (
                <div className="h-[70px] overflow-y-auto">
                  {text}
                </div>
              )}
            />
          </Table>
        )}
      </Flex>
      <Flex gap='small' className="justify-end">
        <Upload
          customRequest={onUploadCsv}
          maxCount={1}
          accept=".csv"
          itemRender={(originNode) => (
            <div className="w-[200px] absolute">{originNode}</div>
          )}
        >
          <Button type="primary" icon={<FileAddOutlined />}>部署をCSV一括登録</Button>
        </Upload>
      </Flex>
      <Divider />
      <div className="flex p-2 text-xl mb-3">
        <ProfileOutlined className="text-[#1677ff]" />
        <span className="pl-2">ユーザ一覧</span>
      </div>
      <div className="mb-1 pr-5 flex justify-end w-full">
        <Badge status="success" />
        <span className="pl-1">アクティブユーザー数: {activeUserValue} / {totalUserValue}</span>
      </div>
      <Flex vertical justify="center" className="relative">
        {isLoadingUsers ? (
          <Flex justify="center" align="center">
            <Spin size="large" tip='Loading...' />
          </Flex>
        ) : (
          <Table<UserResponseType>
            dataSource={userList}
            bordered
            pagination={{
              current: userPagination.current,
              pageSize: userPagination.pageSize,
              total: userPagination.total,
              position: ['bottomCenter'],
            }}
            onChange={handleUserTableChange}
            rowKey={(record) => record.id}
            scroll={{ x: 'max-content' }}
          >
            <Column
              title='部署名'
              dataIndex={['department', 'name']}
              key="department_name"
              onCell={getOnUserCell}
              sorter={true}
              sortOrder={
                currentAntdSortField &&
                  Array.isArray(currentAntdSortField) &&
                  currentAntdSortField.length === 2 &&
                  currentAntdSortField[0] === 'department' &&
                  currentAntdSortField[1] === 'name'
                  ? currentAntdSortOrder
                  : undefined
              }
            />
            <Column
              title='役職'
              dataIndex='position'
              key='position'
              onCell={getOnUserCell}
              sorter={true}
              sortOrder={currentAntdSortField === 'position' ? currentAntdSortOrder : undefined}
            />
            <Column
              title='氏名'
              dataIndex='name'
              key='name'
              onCell={getOnUserCell}
              sorter={true}
              sortOrder={currentAntdSortField === 'name' ? currentAntdSortOrder : undefined}
            />
            <Column title='メールアドレス' dataIndex='email' key='email' onCell={getOnUserCell} />
            <Column
              title='ブランド名'
              dataIndex={['department', 'brand_name']}
              key='brand_name'
              onCell={getOnUserCell}
              sorter={true}
              sortOrder={
                currentAntdSortField &&
                  Array.isArray(currentAntdSortField) &&
                  currentAntdSortField.length === 2 &&
                  currentAntdSortField[0] === 'department' &&
                  currentAntdSortField[1] === 'brand_name'
                  ? currentAntdSortOrder
                  : undefined
              }
            />
            <Column title='PIN' dataIndex='pin_code' key='pin_code' onCell={getOnUserCell} />
            <Column
              title='操作'
              dataIndex='action'
              key='action'
              fixed='right'
              render={(_: any, record: UserResponseType) => (
                <Space size='middle'>
                  <Button variant='outlined' color='primary' onClick={() => onClickManage(record.department.id, record.id ?? '')}>編集</Button>
                  <Button variant='outlined' color={record.is_disabled ? 'green' : 'danger'} onClick={() => onClickToggleStatus(record.id ?? '')}>{record.is_disabled ? '復活' : '停止'}</Button>
                </Space>
              )}
              width='175px'
            />
          </Table>
        )}
      </Flex>
      <Flex gap='small' className="justify-end">
        <Button type="primary" icon={<UserAddOutlined />} onClick={onClickUserRegister}>ユーザ登録</Button>
        <Upload
          customRequest={onUserUploadCsv}
          maxCount={1}
          accept=".csv"
          itemRender={(originNode) => (
            <div className="w-[200px] absolute">{originNode}</div>
          )}
        >
          <Button type="primary" icon={<FileAddOutlined />}>ユーザCSV一括登録</Button>
        </Upload>
        <Button type="primary" icon={<DownloadOutlined />} onClick={onDownloadUsersCsv} loading={isDownloadingUsersCsv}>CSVダウンロード</Button>
      </Flex>
      <Divider />
      <Flex gap="large" align="center" justify="center">
        <Button type="primary" size="large" icon={<ReconciliationOutlined />} onClick={onClickMeasurements}>採寸結果一覧</Button>
        <Button type="primary" size="large" icon={<DatabaseOutlined />} onClick={onClickDeviceList}>デバイス一覧</Button>
        <Button type="primary" size="large" icon={<SlidersOutlined />} onClick={onClickMeasurementsError}>設定誤差一覧</Button>
      </Flex>
    </div>
  );
}
